/**
 * In-memory cache storage
 */

import type { CacheEntry } from '../types'
import type { CacheStorage } from './cache-manager'

/**
 * In-memory cache storage implementation
 */
export class MemoryCache implements CacheStorage {
  private cache = new Map<string, CacheEntry>()

  /**
   * Gets a cache entry
   */
  get<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key)
    return entry ? (entry as CacheEntry<T>) : null
  }

  /**
   * Sets a cache entry
   */
  set<T>(key: string, entry: CacheEntry<T>): void {
    this.cache.set(key, entry)
  }

  /**
   * Deletes a cache entry
   */
  delete(key: string): void {
    this.cache.delete(key)
  }

  /**
   * Clears all cache entries
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Gets the number of cached entries
   */
  size(): number {
    return this.cache.size
  }

  /**
   * Gets all cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys())
  }

  /**
   * Gets all cache values
   */
  values(): CacheEntry[] {
    return Array.from(this.cache.values())
  }

  /**
   * Gets all cache entries
   */
  entries(): Array<[string, CacheEntry]> {
    return Array.from(this.cache.entries())
  }

  /**
   * Checks if a key exists in cache
   */
  has(key: string): boolean {
    return this.cache.has(key)
  }

  /**
   * Iterates over cache entries
   */
  forEach(callback: (entry: CacheEntry, key: string) => void): void {
    this.cache.forEach(callback)
  }

  /**
   * Gets cache statistics
   */
  getStats(): {
    size: number
    memoryUsage: number
  } {
    let memoryUsage = 0

    // Estimate memory usage (rough calculation)
    for (const [key, entry] of this.cache.entries()) {
      memoryUsage += key.length * 2 // UTF-16 characters
      memoryUsage += this.estimateObjectSize(entry)
    }

    return {
      size: this.cache.size,
      memoryUsage,
    }
  }

  /**
   * Estimates the size of an object in bytes
   */
  private estimateObjectSize(obj: any): number {
    let size = 0

    if (obj === null || obj === undefined) {
      return 0
    }

    if (typeof obj === 'string') {
      return obj.length * 2 // UTF-16
    }

    if (typeof obj === 'number') {
      return 8 // 64-bit number
    }

    if (typeof obj === 'boolean') {
      return 4
    }

    if (Array.isArray(obj)) {
      for (const item of obj) {
        size += this.estimateObjectSize(item)
      }
      return size
    }

    if (typeof obj === 'object') {
      for (const [key, value] of Object.entries(obj)) {
        size += key.length * 2 // Key size
        size += this.estimateObjectSize(value) // Value size
      }
      return size
    }

    return 0
  }

  /**
   * Cleans up expired entries
   */
  cleanup(): number {
    let cleaned = 0
    const now = Date.now()

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key)
        cleaned++
      }
    }

    return cleaned
  }

  /**
   * Gets entries sorted by timestamp (oldest first)
   */
  getEntriesByAge(): Array<[string, CacheEntry]> {
    return Array.from(this.cache.entries()).sort(([, a], [, b]) => a.timestamp - b.timestamp)
  }

  /**
   * Removes oldest entries to free up space
   */
  evictOldest(count: number): string[] {
    const evicted: string[] = []
    const sortedEntries = this.getEntriesByAge()

    for (let i = 0; i < count && i < sortedEntries.length; i++) {
      const [key] = sortedEntries[i]!
      this.cache.delete(key)
      evicted.push(key)
    }

    return evicted
  }

  /**
   * Sets maximum cache size with LRU eviction
   */
  setMaxSize(maxSize: number): void {
    if (this.cache.size > maxSize) {
      const toEvict = this.cache.size - maxSize
      this.evictOldest(toEvict)
    }
  }

  /**
   * Gets cache hit ratio (requires tracking)
   */
  getHitRatio(): number {
    // This would require additional tracking of hits/misses
    // For now, return 0 as placeholder
    return 0
  }

  /**
   * Exports cache data for serialization
   */
  export(): Record<string, CacheEntry> {
    const exported: Record<string, CacheEntry> = {}

    for (const [key, entry] of this.cache.entries()) {
      exported[key] = entry
    }

    return exported
  }

  /**
   * Imports cache data from serialized format
   */
  import(data: Record<string, CacheEntry>): void {
    this.cache.clear()

    for (const [key, entry] of Object.entries(data)) {
      this.cache.set(key, entry)
    }
  }

  /**
   * Creates a snapshot of current cache state
   */
  snapshot(): Map<string, CacheEntry> {
    return new Map(this.cache)
  }

  /**
   * Restores cache from a snapshot
   */
  restore(snapshot: Map<string, CacheEntry>): void {
    this.cache = new Map(snapshot)
  }
}
